/**
 * Los atajos de la rejilla de capturas, como datos y no como cinco cierres.
 *
 * Antes cada atajo era un `poner()` que llamaba a seis `set*`, y cinco de esas llamadas
 * existían solo para APAGAR lo que había puesto otro atajo. Bastaba olvidar una para que
 * dos atajos quedaran cruzados y la rejilla enseñara la intersección sin que nadie la
 * hubiera pedido — y pasó: "Explosivos" no limpiaba las copias.
 *
 * Aquí cada atajo declara el estado COMPLETO, así que no hay nada que acordarse de apagar:
 * ponerlo es asignar, y la exclusividad sale sola. Añadir un filtro nuevo a `EstadoAtajo`
 * obliga a darle valor en todos, que es justo lo que el olvido rompía.
 */

/** Los filtros que los atajos gobiernan. El resto —búsqueda, tipo, estado— es tuyo. */
export interface EstadoAtajo {
  sortBy: string;
  minVelocity: string;
  minAdvertiserAds: string;
  minDays: string;
  minCopies: string;
  desdeHoras: string;
  /** La papelera no es un umbral sino un sitio, pero comparte exclusividad con el resto. */
  papelera: boolean;
}

/** Sin ningún atajo puesto: lo que queda al apagar el que estuviera. */
export const SIN_ATAJO: EstadoAtajo = {
  sortBy: 'velocity',
  minVelocity: '',
  minAdvertiserAds: '',
  minDays: '',
  minCopies: '',
  desdeHoras: '',
  papelera: false
};

export interface Atajo {
  id: string;
  /** El icono va aparte del texto: por debajo de 1280 px solo cabe él. */
  icono: string;
  label: string;
  hint: string;
  estado: EstadoAtajo;
}

export const ATAJOS: Atajo[] = [
  {
    id: 'mega',
    icono: '👑',
    label: 'Mega ganadores',
    hint: 'Anunciantes con máquina montada: pesa el tamaño de la operación casi el doble que las copias, y luego el tiempo corriendo',
    estado: { ...SIN_ATAJO, sortBy: 'mega' }
  },
  {
    id: 'nuevas',
    icono: '🆕',
    label: 'Nuevas de hoy',
    hint: 'Capturadas en las últimas 24 horas, lo más reciente primero',
    estado: { ...SIN_ATAJO, sortBy: 'captured', desdeHoras: '24' }
  },
  {
    id: 'explosivos',
    icono: '🔥',
    label: 'Explosivos',
    hint: 'Ganan 2+ copias al día y su anunciante corre 50+ anuncios activos',
    estado: { ...SIN_ATAJO, sortBy: 'velocity', minVelocity: '2', minAdvertiserAds: '50' }
  },
  {
    id: 'probados',
    icono: '🏆',
    label: 'Probados',
    hint: '90+ días corriendo y 5+ copias: el presupuesto de otro ya los validó',
    estado: { ...SIN_ATAJO, sortBy: 'longest', minDays: '90', minCopies: '5' }
  },
  {
    id: 'papelera',
    icono: '🗑',
    label: 'Papelera',
    hint: 'Lo que descartaste tú, lo último primero, con el botón de recuperar',
    estado: { ...SIN_ATAJO, sortBy: 'dismissed', papelera: true }
  }
];

/**
 * Cuál de los atajos describe el estado actual, o null si es una combinación tuya.
 *
 * La papelera se reconoce por estar dentro y no por coincidir entera: es un sitio, y
 * cambiar el orden mientras estás en ella no debe apagar el botón por el que entraste.
 */
export function atajoActivo(e: EstadoAtajo): string | null {
  if (e.papelera) return 'papelera';
  const iguales = (a: EstadoAtajo, b: EstadoAtajo) =>
    (Object.keys(SIN_ATAJO) as Array<keyof EstadoAtajo>).every(k => a[k] === b[k]);
  return ATAJOS.find(a => !a.estado.papelera && iguales(a.estado, e))?.id ?? null;
}
